import { create } from "zustand"
import { apiService } from "@/lib/api"
import { AUTH_API } from "@/constants/api-paths"
import {
  RequestPasswordResetCodeRequest,
  VerifyPasswordResetCodeRequest,
  VerifyPasswordResetCodeResponse,
  ResetPasswordRequest,
} from "@/models/auth"

// ===== Password Reset State Interface =====
interface PasswordResetState {
  loading: boolean
  error: string | null

  // 액션
  requestCode: (email: string) => Promise<{ success: boolean }>
  verifyCode: (email: string, verificationCode: string) => Promise<{ success: boolean; resetToken?: string }>
  resetPassword: (resetToken: string, newPassword: string, newPasswordConfirm: string) => Promise<{ success: boolean }>
  clearError: () => void
}

// ===== Zustand Store =====
export const usePasswordResetStore = create<PasswordResetState>()((set) => ({
  // 초기 상태
  loading: false,
  error: null,

  // 인증번호 발송
  requestCode: async (email: string) => {
    set({ loading: true, error: null })
    try {
      await apiService.post<void>(AUTH_API.PASSWORD_RESET_CODE, { email } satisfies RequestPasswordResetCodeRequest)
      return { success: true }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '인증번호 발송에 실패했습니다.'
      set({ error: errorMsg })
      return { success: false }
    } finally {
      set({ loading: false })
    }
  },

  // 인증번호 확인
  verifyCode: async (email: string, verificationCode: string) => {
    set({ loading: true, error: null })
    try {
      const response = await apiService.post<VerifyPasswordResetCodeResponse>(
        AUTH_API.PASSWORD_RESET_CODE_VERIFY,
        { email, verificationCode } satisfies VerifyPasswordResetCodeRequest
      )
      return { success: true, resetToken: response.resetToken }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '인증번호 확인에 실패했습니다.'
      set({ error: errorMsg })
      return { success: false }
    } finally {
      set({ loading: false })
    }
  },

  // 비밀번호 재설정
  resetPassword: async (resetToken: string, newPassword: string, newPasswordConfirm: string) => {
    set({ loading: true, error: null })
    try {
      await apiService.put<void>(AUTH_API.PASSWORD_RESET, {
        resetToken,
        newPassword,
        newPasswordConfirm,
      } satisfies ResetPasswordRequest)
      return { success: true }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '비밀번호 재설정에 실패했습니다.'
      set({ error: errorMsg })
      return { success: false }
    } finally {
      set({ loading: false })
    }
  },

  // 에러 초기화
  clearError: () => set({ error: null }),
}))
